/**
 * NeverMissABeat — Complete Theme System (6 Themes)
 *
 * Usage in Rails:
 * - Store user's theme preference in database
 * - Pass theme to mailer: `DigestMailer.weekly_digest(user, events, theme: user.email_theme)`
 * - Render with: `render 'digest_email', theme: theme`
 *
 * Themes:
 * - Electric Purple (dark, bold, music-forward)
 * - Neon Cyan (dark, futuristic)
 * - Deep Orange (dark, warm stage-light vibes)
 * - Rose Gold (dark, premium/intimate)
 * - Light (bright, clean, daytime reading)
 * - High Contrast (WCAG AAA compliant)
 */

export interface ThemeColors {
  brand: {
    primary: string
    primaryHover: string
    primaryMuted: string
    secondary: string
    secondaryHover: string
  }
  surface: {
    base: string
    raised: string
    overlay?: string
    hover: string
    border: string
    borderSubtle: string
  }
  text: {
    primary: string
    secondary: string
    muted: string
    inverse?: string
    link?: string
  }
  status: {
    success: string
    successMuted?: string
    warning: string
    warningMuted?: string
    error: string
    errorMuted?: string
    info?: string
    infoMuted?: string
  }
  interactive?: {
    focus: string
    selected: string
    selectedMuted: string
    disabled: string
  }
}

export interface Theme {
  id?: string
  name: string
  description: string
  colors: ThemeColors
}

// ============================================================================
// DARK THEMES
// ============================================================================

export const electricPurple: Theme = {
  id: 'electric-purple',
  name: 'Electric Purple',
  description: 'Bold, modern, music-focused. Purple primary with hot pink secondary.',
  colors: {
    brand: {
      primary: '#7c3aed',
      primaryHover: '#a855f7',
      primaryMuted: 'rgba(124, 58, 237, 0.12)',
      secondary: '#ff006e',
      secondaryHover: '#ff3385',
    },
    surface: {
      base: '#0a0806',
      raised: '#1a1714',
      hover: '#252219',
      border: '#3a3530',
      borderSubtle: '#2a251f',
    },
    text: {
      primary: '#f5f5f5',
      secondary: '#b8b3aa',
      muted: '#7a7370',
    },
    status: {
      success: '#10b981',
      warning: '#f97316',
      error: '#ef4444',
    },
  },
}

export const neonCyan: Theme = {
  id: 'neon-cyan',
  name: 'Neon Cyan',
  description: 'Futuristic festival vibes. Cyan primary with rose accent.',
  colors: {
    brand: {
      primary: '#06b6d4',
      primaryHover: '#22d3ee',
      primaryMuted: 'rgba(6, 182, 212, 0.12)',
      secondary: '#f43f5e',
      secondaryHover: '#fb7185',
    },
    surface: {
      base: '#0a0a0f',
      raised: '#12121a',
      hover: '#1a1a28',
      border: '#2a2a3a',
      borderSubtle: '#1a1a28',
    },
    text: {
      primary: '#f0f0f5',
      secondary: '#a0a0b5',
      muted: '#6b6b80',
    },
    status: {
      success: '#10b981',
      warning: '#fbbf24',
      error: '#ef4444',
    },
  },
}

export const deepOrange: Theme = {
  id: 'deep-orange',
  name: 'Deep Orange',
  description: 'Warm stage-light aesthetic. Orange primary with amber and purple.',
  colors: {
    brand: {
      primary: '#ea580c',
      primaryHover: '#f97316',
      primaryMuted: 'rgba(234, 88, 12, 0.12)',
      secondary: '#fbbf24',
      secondaryHover: '#fcd34d',
    },
    surface: {
      base: '#0f0a08',
      raised: '#1a1410',
      hover: '#241d18',
      border: '#3a2f28',
      borderSubtle: '#2a231c',
    },
    text: {
      primary: '#faf8f6',
      secondary: '#bfb3a8',
      muted: '#7a6f66',
    },
    status: {
      success: '#8b5cf6',
      warning: '#f97316',
      error: '#dc2626',
    },
  },
}

export const roseGold: Theme = {
  id: 'rose-gold',
  name: 'Rose Gold',
  description: 'Premium, intimate. Rose primary with lavender and coral.',
  colors: {
    brand: {
      primary: '#be185d',
      primaryHover: '#ec4899',
      primaryMuted: 'rgba(190, 24, 93, 0.12)',
      secondary: '#ddd6fe',
      secondaryHover: '#ede9fe',
    },
    surface: {
      base: '#0a0505',
      raised: '#1a0f15',
      hover: '#24141f',
      border: '#3a2530',
      borderSubtle: '#2a1a25',
    },
    text: {
      primary: '#faf5f8',
      secondary: '#bfa8b3',
      muted: '#7a6a75',
    },
    status: {
      success: '#f472b6',
      warning: '#fca5a5',
      error: '#f87171',
    },
  },
}

// ============================================================================
// LIGHT THEMES
// ============================================================================

export const lightTheme: Theme = {
  id: 'light',
  name: 'Light',
  description: 'Clean, bright theme for comfortable daytime reading',
  colors: {
    brand: {
      primary: '#4f46e5',
      primaryHover: '#6366f1',
      primaryMuted: 'rgba(79, 70, 229, 0.1)',
      secondary: '#db2777',
      secondaryHover: '#ec4899',
    },
    surface: {
      base: '#f8f9fb',
      raised: '#ffffff',
      overlay: '#ffffff',
      hover: '#f0f1f4',
      border: '#d4d6dc',
      borderSubtle: '#e5e7eb',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      muted: '#9ca3af',
      inverse: '#ffffff',
      link: '#4f46e5',
    },
    status: {
      success: '#16a34a',
      successMuted: 'rgba(22, 163, 74, 0.1)',
      warning: '#ca8a04',
      warningMuted: 'rgba(202, 138, 4, 0.1)',
      error: '#dc2626',
      errorMuted: 'rgba(220, 38, 38, 0.1)',
      info: '#2563eb',
      infoMuted: 'rgba(37, 99, 235, 0.1)',
    },
    interactive: {
      focus: '#4f46e5',
      selected: '#4f46e5',
      selectedMuted: 'rgba(79, 70, 229, 0.1)',
      disabled: '#d1d5db',
    },
  },
}

export const highContrastTheme: Theme = {
  id: 'high-contrast',
  name: 'High Contrast',
  description: 'Enhanced contrast for improved readability (WCAG AAA)',
  colors: {
    brand: {
      primary: '#7c7fff',
      primaryHover: '#9b9eff',
      primaryMuted: 'rgba(124, 127, 255, 0.2)',
      secondary: '#ff6eb4',
      secondaryHover: '#ff8ec8',
    },
    surface: {
      base: '#000000',
      raised: '#111111',
      overlay: '#1a1a1a',
      hover: '#2a2a2a',
      border: '#444444',
      borderSubtle: '#333333',
    },
    text: {
      primary: '#ffffff',
      secondary: '#cccccc',
      muted: '#999999',
      inverse: '#ffffff',
      link: '#9b9eff',
    },
    status: {
      success: '#4ade80',
      successMuted: 'rgba(74, 222, 128, 0.2)',
      warning: '#facc15',
      warningMuted: 'rgba(250, 204, 21, 0.2)',
      error: '#f87171',
      errorMuted: 'rgba(248, 113, 113, 0.2)',
      info: '#60a5fa',
      infoMuted: 'rgba(96, 165, 250, 0.2)',
    },
    interactive: {
      focus: '#7c7fff',
      selected: '#7c7fff',
      selectedMuted: 'rgba(124, 127, 255, 0.2)',
      disabled: '#555555',
    },
  },
}

// ============================================================================
// THEME CONSTANTS & HELPERS
// ============================================================================

export const THEMES = {
  electricPurple,
  neonCyan,
  deepOrange,
  roseGold,
  light: lightTheme,
  highContrast: highContrastTheme,
}

export const defaultTheme = electricPurple

/**
 * Get a theme by ID
 * @param themeId - Theme ID (e.g., 'electric-purple', 'light', 'high-contrast')
 * @returns Theme object or default (Electric Purple)
 */
export function getTheme(themeId?: string): Theme {
  if (!themeId) return defaultTheme

  const theme = Object.values(THEMES).find((t) => t.id === themeId)
  return theme || defaultTheme
}

/**
 * Get all available themes
 */
export function getAllThemes(): Theme[] {
  return Object.values(THEMES)
}

/**
 * Get themes by category (dark/light)
 */
export function getThemesByCategory(category: 'dark' | 'light'): Theme[] {
  const darkThemes = ['electric-purple', 'neon-cyan', 'deep-orange', 'rose-gold']
  const lightThemes = ['light', 'high-contrast']

  const themeIds = category === 'dark' ? darkThemes : lightThemes

  return themeIds
    .map((id) => Object.values(THEMES).find((t) => t.id === id))
    .filter((t): t is Theme => t !== undefined)
}

/**
 * Convert theme to CSS variables for inline email styles
 * Used in Rails mailer to pass theme to ERB template
 */
export function themeToEmailVariables(theme: Theme): Record<string, string> {
  return {
    bgBase: theme.colors.surface.base,
    bgRaised: theme.colors.surface.raised,
    bgHover: theme.colors.surface.hover,
    border: theme.colors.surface.border,
    borderSubtle: theme.colors.surface.borderSubtle,

    textPrimary: theme.colors.text.primary,
    textSecondary: theme.colors.text.secondary,
    textMuted: theme.colors.text.muted,

    brandPrimary: theme.colors.brand.primary,
    brandPrimaryHover: theme.colors.brand.primaryHover,
    brandPrimaryMuted: theme.colors.brand.primaryMuted,
    brandSecondary: theme.colors.brand.secondary,
    brandSecondaryHover: theme.colors.brand.secondaryHover,

    statusSuccess: theme.colors.status.success,
    statusWarning: theme.colors.status.warning,
    statusError: theme.colors.status.error,
  }
}

// ============================================================================
// THEME APPLICATION (Frontend)
// ============================================================================

/**
 * Applies a theme by writing its color values to CSS custom properties
 * on the document root. Every CSS class referencing these variables
 * will update instantly.
 *
 * Usage:
 *   import { applyTheme, defaultTheme } from '@/lib/theme'
 *   applyTheme(defaultTheme)
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  const { colors } = theme

  // Brand
  root.style.setProperty('--brand-primary', colors.brand.primary)
  root.style.setProperty('--brand-primary-hover', colors.brand.primaryHover)
  root.style.setProperty('--brand-primary-muted', colors.brand.primaryMuted)
  root.style.setProperty('--brand-secondary', colors.brand.secondary)
  root.style.setProperty('--brand-secondary-hover', colors.brand.secondaryHover)

  // Surfaces
  root.style.setProperty('--surface-base', colors.surface.base)
  root.style.setProperty('--surface-raised', colors.surface.raised)
  root.style.setProperty('--surface-overlay', colors.surface.overlay || colors.surface.raised)
  root.style.setProperty('--surface-hover', colors.surface.hover)
  root.style.setProperty('--surface-border', colors.surface.border)
  root.style.setProperty('--surface-border-subtle', colors.surface.borderSubtle)

  // Text
  root.style.setProperty('--text-primary', colors.text.primary)
  root.style.setProperty('--text-secondary', colors.text.secondary)
  root.style.setProperty('--text-muted', colors.text.muted)
  root.style.setProperty('--text-inverse', colors.text.inverse || '#ffffff')
  root.style.setProperty('--text-link', colors.text.link || colors.brand.primary)

  // Status
  root.style.setProperty('--status-success', colors.status.success)
  root.style.setProperty(
    '--status-success-muted',
    colors.status.successMuted || `${colors.status.success}20`,
  )
  root.style.setProperty('--status-warning', colors.status.warning)
  root.style.setProperty(
    '--status-warning-muted',
    colors.status.warningMuted || `${colors.status.warning}20`,
  )
  root.style.setProperty('--status-error', colors.status.error)
  root.style.setProperty(
    '--status-error-muted',
    colors.status.errorMuted || `${colors.status.error}20`,
  )
  root.style.setProperty('--status-info', colors.status.info || colors.brand.primary)
  root.style.setProperty(
    '--status-info-muted',
    colors.status.infoMuted || `${colors.brand.primary}20`,
  )

  // Interactive (if available)
  if (colors.interactive) {
    root.style.setProperty('--interactive-focus', colors.interactive.focus)
    root.style.setProperty('--interactive-selected', colors.interactive.selected)
    root.style.setProperty('--interactive-selected-muted', colors.interactive.selectedMuted)
    root.style.setProperty('--interactive-disabled', colors.interactive.disabled)
  }
}

// ============================================================================
// THEME PERSISTENCE (localStorage)
// ============================================================================

const THEME_STORAGE_KEY = 'nmab-theme'

/** Retrieve the stored theme name, or null if none saved */
export function getStoredThemeName(): string | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY)
  } catch {
    return null
  }
}

/** Save the current theme name to localStorage */
export function setStoredThemeName(name: string): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, name)
  } catch {
    // localStorage unavailable — ignore silently
  }
}

/** Load and apply the stored theme, falling back to the default theme */
export function applyStoredTheme(): Theme {
  const storedName = getStoredThemeName()
  const themeList = getAllThemes()
  const theme = (storedName && themeList.find((t) => t.name === storedName)) || defaultTheme
  applyTheme(theme)
  return theme
}

// ============================================================================
// THEME SWITCHER UI HELPERS
// ============================================================================

/** All available themes for a theme-switcher UI */
export const themes: Theme[] = getAllThemes()

/** Map of theme names to theme objects for quick lookup */
export const themeMap: Record<string, Theme> = Object.fromEntries(themes.map((t) => [t.name, t]))

/** Map of theme IDs to theme objects */
export const themeIdMap: Record<string, Theme> = Object.fromEntries(
  themes.map((t) => [t.id || t.name, t]),
)
